import { useEffect, useRef } from "react";

/**
 * Shared stacking manager for overlay surfaces (modals, drawers).
 *
 * The problem it solves: overlays used to hard-code z-index values, so which
 * one sat on top depended on which numbers the authors happened to pick — a
 * modal opened *from* another modal could render behind its own parent
 * (AssignRepModal's shell is `.confirmation-modal`, z-index 10000, while
 * AnimatedModal defaulted to 1200).
 *
 * Instead, every surface asks for a layer as it opens and gets one above all
 * currently-open surfaces, so the most recently opened is always on top.
 *
 * The counter resets once everything closes, so z-index never grows unbounded
 * across a long session.
 */

/**
 * Layers start above every persistent page-level surface that does NOT take
 * part in this stack, so an overlay is never trapped underneath page chrome.
 * The tallest of those is the calendar's DayDetailPanel at 1300 (see
 * calendarRedesign/components/DayDetailPanel.css); ClinicHeader is 1100.
 *
 * It stays below the 9990–10000 band used by confirm/success/error dialogs and
 * the purchase banner, which are meant to sit on top of everything.
 *
 * If you add fixed page chrome above 1300, raise this — don't lower the chrome.
 */
export const MODAL_LAYER_BASE = 3000;
const MODAL_LAYER_STEP = 10;

let openCount = 0;
let sequence = 0;

const acquireLayer = (): number => {
  openCount += 1;
  sequence += 1;
  return MODAL_LAYER_BASE + sequence * MODAL_LAYER_STEP;
};

const releaseLayer = (): void => {
  openCount = Math.max(0, openCount - 1);
  if (openCount === 0) sequence = 0;
};

/**
 * Returns the z-index this surface should render at while `isOpen`.
 *
 * @param isOpen        whether the surface is currently shown
 * @param explicitZIndex caller-supplied override — when provided, that value is
 *                       used verbatim and no layer is taken from the stack
 */
export const useModalLayer = (
  isOpen: boolean,
  explicitZIndex?: number,
): number => {
  const layerRef = useRef<number | null>(null);

  // Claimed during render (not in an effect) so the very first painted frame
  // already carries the correct z-index — assigning it in an effect would show
  // one frame at the wrong depth.
  if (isOpen && explicitZIndex === undefined && layerRef.current === null) {
    layerRef.current = acquireLayer();
  }

  useEffect(() => {
    if (!isOpen && layerRef.current !== null) {
      releaseLayer();
      layerRef.current = null;
    }
  }, [isOpen]);

  // Release on unmount too — surfaces are often unmounted while still open.
  useEffect(() => {
    return () => {
      if (layerRef.current !== null) {
        releaseLayer();
        layerRef.current = null;
      }
    };
  }, []);

  if (explicitZIndex !== undefined) return explicitZIndex;
  return layerRef.current ?? MODAL_LAYER_BASE;
};
